import {useMemo} from "react";
import {useQueryClient} from "@tanstack/react-query";
import {useCustomQuery} from "~/query/queryClient";
import {queryHttp} from "~/query/queryHttp";
import {profileHeaderUrl, shouldCostUrl} from "../services/categoryProfileUrls";
import type {LineInput, ModelInput, OutcomeInput, PriceModel, ShouldCost} from "../types/shouldCostTypes";
import {done, failed} from "./notices";

type Answer = {id: number | null; shouldCost: ShouldCost};

export function useShouldCostQuery(databagId: number, path: string) {
    const url = shouldCostUrl(databagId, path);
    return useCustomQuery({
        queryKey: [url] as const,
        queryFn: async () => (await queryHttp.get<Answer>(url)).data.shouldCost,
        enabled: databagId > 0 && Boolean(path),
        retry: false,
    });
}

/** Model and quote writes with notices and Undo. Adds and edits reject on failure for the dialog to show. */
export function useShouldCostActions(databagId: number, path: string) {
    const qc = useQueryClient();
    return useMemo(() => {
        const write = async (method: 'post' | 'patch' | 'delete', sub: string, data?: unknown, multipart = false) => {
            const res = await queryHttp.request<Answer>({method, url: shouldCostUrl(databagId, path, sub), data,
                ...(multipart && {headers: {'Content-Type': 'multipart/form-data'}})});
            qc.setQueryData([shouldCostUrl(databagId, path)], res.data.shouldCost);
            // The tab count (opportunities) lives in the header.
            void qc.invalidateQueries({predicate: q => String(q.queryKey[0]).startsWith(profileHeaderUrl(databagId, path).split('?')[0])});
            return res.data;
        };
        const guarded = async (fn: () => Promise<unknown>) => {
            try {
                await fn();
            } catch (e) {
                failed(e);
            }
        };
        return {
            addModel: async (input: ModelInput) => {
                const res = await write('post', '', input);
                done('Price model created.', () => write('delete', String(res.id)));
            },
            updateModel: async (model: PriceModel, change: Partial<ModelInput>) => {
                await write('patch', String(model.id), change);
                done('Price model saved.', () => write('patch', String(model.id), {
                    name: model.name, unit: model.unit, currency: model.currency, baseMonth: model.baseMonth,
                    annualVolume: model.annualVolume, lines: linesOf(model),
                }));
            },
            setActive: (model: PriceModel) => guarded(async () => {
                await write('patch', String(model.id), {active: true});
                done(`${model.name} is now the active model.`);
            }),
            removeModel: (model: PriceModel) => guarded(async () => {
                await write('delete', String(model.id));
                done(`${model.name} removed.`, () => write('post', `${model.id}/restore`));
            }),
            addOutcome: async (model: PriceModel, input: OutcomeInput) => {
                const res = await write('post', `${model.id}/outcomes`, input);
                done('Negotiation outcome recorded.', () => write('delete', `outcomes/${res.id}`));
            },
            removeOutcome: (id: number, supplier: string) => guarded(async () => {
                await write('delete', `outcomes/${id}`);
                done(`Outcome with ${supplier} removed.`, () => write('post', `outcomes/${id}`));
            }),
        };
    }, [qc, databagId, path]);
}

export type ShouldCostActions = ReturnType<typeof useShouldCostActions>;

/** A model's lines as they are saved, to put them back (Undo). */
function linesOf(model: PriceModel): LineInput[] {
    return model.lines.map(l => ({id: l.id, name: l.name, baseCost: l.baseCost, seriesId: l.series?.id ?? null, tier: l.tier,
        kind: l.kind, assumption: l.assumption, calibrationPct: l.calibrationPct}));
}
